import React from 'react';
import type { TFunction } from 'i18next';

const TOTAL_SECONDS = 10 * 60;

interface MappingGenerationPlaceholderProps {
    t: TFunction;
}

/**
 * Affichage lorsque la cartographie est encore générée côté serveur (is_finish === false).
 * Décompte 10 min + étapes + barre de progression indéterminée.
 */
const MappingGenerationPlaceholder: React.FC<MappingGenerationPlaceholderProps> = ({ t }) => {
    const [remaining, setRemaining] = React.useState(TOTAL_SECONDS);
    const [stepIdx, setStepIdx] = React.useState(0);

    const steps = React.useMemo(
        () => [
            t('features.swimlane.mapGenStep1'),
            t('features.swimlane.mapGenStep2'),
            t('features.swimlane.mapGenStep3'),
        ],
        [t],
    );

    React.useEffect(() => {
        const id = window.setInterval(() => {
            setRemaining((r) => Math.max(0, r - 1));
        }, 1000);
        return () => window.clearInterval(id);
    }, []);

    React.useEffect(() => {
        const id = window.setInterval(() => {
            setStepIdx((i) => (i + 1) % steps.length);
        }, 4000);
        return () => window.clearInterval(id);
    }, [steps.length]);

    const mm = Math.floor(remaining / 60);
    const ss = remaining % 60;
    const pct = 1 - remaining / TOTAL_SECONDS;

    return (
        <div className="mapping-gen-placeholder">
            <div className="mapping-gen-inner">
                <div className="mapping-gen-spinner" aria-hidden />
                <h2 className="mapping-gen-title">{t('features.swimlane.mapGenTitle')}</h2>
                <p className="mapping-gen-sub">{t('features.swimlane.mapGenSubtitle')}</p>
                <p className="mapping-gen-step" key={stepIdx}>
                    {steps[stepIdx]}
                </p>
                <div className="mapping-gen-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct * 100)}>
                    <div className="mapping-gen-progress-fill" style={{ width: `${Math.min(100, pct * 100)}%` }} />
                </div>
                <p className="mapping-gen-timer">
                    {t('features.swimlane.mapGenCountdown', {
                        mm: String(mm).padStart(2, '0'),
                        ss: String(ss).padStart(2, '0'),
                    })}
                </p>
            </div>
        </div>
    );
};

export default MappingGenerationPlaceholder;
